import { useState, useRef } from 'react';
import './PhotoGeneration.css';
import GenerationControls from '../components/GenerationControls';
import ResultDisplay from '../components/ResultDisplay';
import PaymentPopup from '../popups/PaymentPopup';
import AnimationPopup from '../popups/AnimationPopup';
import EditPopup from '../popups/EditPopup';
import { useTaskPolling } from "@genfun/core";

export default function PhotoGeneration({ token, user, setSignedIn, setShowLoginPopup }) {
    // Generation state
    const [img, setImg] = useState(null);           // Array of image URLs
    const [video, setVideo] = useState(null);       // Video URL
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [taskId, setTaskId] = useState(null);
    const [generated, setGenerated] = useState(false);
    const [genType, setGenType] = useState(null);   // 'photo' | 'video' | null
    const [isProcessing, setIsProcessing] = useState(false); // true during animate/edit
    const [animateAction, setAnimateAction] = useState(null);
    const [generationConfig, setGenerationConfig] = useState(null);

    // Popup state
    const [showPayPopUp, setShowPayPopUp] = useState(false);
    const [showVideoPopUp, setShowVideoPopUp] = useState(false);
    const [showAnimationPopup, setShowAnimationPopup] = useState(false);
    const [showEditPopup, setShowEditPopup] = useState(false);
    const [actionBatchIdx, setActionBatchIdx] = useState(0);

    // Results ref for scrolling
    const resultsRef = useRef(null);

    // Reset selected index when new images are generated
    const handleSetImg = (images) => {
        setImg(images);
        setSelectedIndex(0);
    };

    // Scroll to results
    const scrollToResults = () => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    // Action button handlers
    const handleAnimateClick = (batchIdx) => {
        setActionBatchIdx(batchIdx);
        setShowAnimationPopup(true);
    };

    const handleEditClick = (batchIdx) => {
        setActionBatchIdx(batchIdx);
        setShowEditPopup(true);
    };

    // Action polling state - use ref to avoid stale closure in callbacks
    const pendingActionTypeRef = useRef(null);
    const actionTaskIdRef = useRef(null);

    // Polling callbacks for animate/edit
    const pollingCallbacks = {
        onSuccess: (status) => {
            setGenType(null);
            setIsProcessing(false);
            const actionType = pendingActionTypeRef.current;
            const newTaskId = actionTaskIdRef.current;

            // API returns status.result.output.message (array of URLs)
            if (actionType === 'animate' && status.result?.output?.message) {
                setImg(null);  // Clear images when showing video
                // For video, message[0] is the video URL
                const videoUrl = Array.isArray(status.result.output.message)
                    ? status.result.output.message[0]
                    : status.result.output.message;
                setVideo(videoUrl);
                setTaskId(newTaskId);
                setGenerated(true);
            } else if (actionType === 'edit' && status.result?.output?.message) {
                setVideo(null);  // Clear video when showing images
                // For edit, message is array of image URLs
                const urls = Array.isArray(status.result.output.message)
                    ? status.result.output.message
                    : [status.result.output.message];
                handleSetImg(urls);
                setTaskId(newTaskId);
                setGenerated(true);
            }
            pendingActionTypeRef.current = null;
            actionTaskIdRef.current = null;
        },
        onError: (errorStatus, statusObj) => {
            setGenType(null);
            setIsProcessing(false);
            pendingActionTypeRef.current = null;
            actionTaskIdRef.current = null;
            console.error('Action failed:', errorStatus, statusObj);
        },
        onPollingError: (err) => {
            setGenType(null);
            setIsProcessing(false);
            pendingActionTypeRef.current = null;
            actionTaskIdRef.current = null;
            console.error('Polling error:', err);
        }
    };

    const { startPolling: startActionPolling } = useTaskPolling(token, pollingCallbacks);

    // Animate/Edit completion handlers
    const handleAnimateStart = () => {
        setGenType('video');
        setIsProcessing(true);
        pendingActionTypeRef.current = 'animate';
        scrollToResults();
    };

    const handleAnimateComplete = (newTaskId) => {
        actionTaskIdRef.current = newTaskId;
        startActionPolling(newTaskId);
    };

    const handleEditStart = () => {
        setGenType('photo');
        setIsProcessing(true);
        pendingActionTypeRef.current = 'edit';
        scrollToResults();
    };

    const handleEditComplete = (newTaskId) => {
        actionTaskIdRef.current = newTaskId;
        startActionPolling(newTaskId);
    };

    const handleActionError = (message) => {
        setGenType(null);
        setIsProcessing(false);
        pendingActionTypeRef.current = null;
        console.error(message);
    };

    return (
        <div className="photoGenerationPage">
            <div className="photoGenerationResults" ref={resultsRef}>
                <ResultDisplay
                    images={img}
                    video={video}
                    selectedIndex={selectedIndex}
                    onSelectIndex={setSelectedIndex}
                    genType={genType}
                    generated={generated}
                    isProcessing={isProcessing}
                    token={token}
                    taskId={taskId}
                    onAnimateClick={handleAnimateClick}
                    onEditClick={handleEditClick}
                />
            </div>

            <div className="photoGenerationControls">
                <GenerationControls
                    token={token}
                    user={user}
                    setImg={handleSetImg}
                    setVideo={setVideo}
                    setGenType={setGenType}
                    setGenerated={setGenerated}
                    setTaskId={setTaskId}
                    setAnimateAction={setAnimateAction}
                    setGenerationConfig={setGenerationConfig}
                    setShowPayPopUp={setShowPayPopUp}
                    setShowVideoPopUp={setShowVideoPopUp}
                    setShowLoginPopup={setShowLoginPopup}
                    onScrollToResults={scrollToResults}
                />
            </div>

            <PaymentPopup
                isOpen={showPayPopUp}
                setIsOpen={setShowPayPopUp}
                token={token}
            />

            <AnimationPopup
                isOpen={showAnimationPopup}
                setIsOpen={setShowAnimationPopup}
                token={token}
                taskId={taskId}
                batchIdx={actionBatchIdx}
                onAnimateStart={handleAnimateStart}
                onAnimateComplete={handleAnimateComplete}
                onAnimateError={handleActionError}
                onPaymentRequired={() => setShowPayPopUp(true)}
                generationConfig={generationConfig}
            />

            <EditPopup
                isOpen={showEditPopup}
                setIsOpen={setShowEditPopup}
                token={token}
                taskId={taskId}
                batchIdx={actionBatchIdx}
                onEditStart={handleEditStart}
                onEditComplete={handleEditComplete}
                onEditError={handleActionError}
                onPaymentRequired={() => setShowPayPopUp(true)}
            />
        </div>
    );
}
